import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

import { formatWeekCommencingDate } from "../../Util/HelperFunction";

const OrganizationStatsChart = ({ teacherStatsData }) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      if (!Array.isArray(entries) || !entries.length) return;
      const entry = entries[0];
      setWidth(entry.contentRect.width);
    });
    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
    };
  }, []);

  const colors = [
    "#2B3674",
    "#D200A5",
    "#707EAE",
    "#FF8042",
    "#0088FE",
    "#00C49F",
    "#FFBB28",
  ];


  const orgKeys = useMemo(() => {
    if (!teacherStatsData || teacherStatsData.length === 0) return [];
    return [
      ...new Set(
        teacherStatsData.flatMap((item) =>
          Object.keys(item).filter((key) => key !== "date")
        )
      ),
    ];
  }, [teacherStatsData]);


  const processedData = useMemo(() => {
    if (!teacherStatsData || teacherStatsData.length === 0) return [];


    const dataWithAllKeys = teacherStatsData.map((item) => {
      const processedItem = { ...item };
      orgKeys.forEach((key) => {
        if (!(key in processedItem)) {
          processedItem[key] = 0;
        }
      });
      return processedItem;
    });


    const cumulativeData = [];
    const runningTotals = {};

    orgKeys.forEach((key) => {
      runningTotals[key] = 0;
    });

    dataWithAllKeys.forEach((item) => {
      const cumulativeItem = { ...item };

      orgKeys.forEach((key) => {
        runningTotals[key] += Number(item[key]) || 0;
        cumulativeItem[key] = runningTotals[key];
      });

      cumulativeData.push(cumulativeItem);
    });

    return cumulativeData;
  }, [teacherStatsData, orgKeys]);

  const maxVal = useMemo(() => {
    if (!processedData || processedData.length === 0) return 15;
    let max = 0;
    processedData.forEach((item) => {
      orgKeys.forEach((key) => {
        const val = Number(item[key]);
        if (!isNaN(val) && val > max) {
          max = val;
        }
      });
    });
    return max;
  }, [processedData, orgKeys]);

  const yAxisTicks = useMemo(() => {
    let limit = Math.max(15, maxVal);

    let step = 3;
    if (limit > 2000) step = 500;
    else if (limit > 1000) step = 200;
    else if (limit > 500) step = 100;
    else if (limit > 200) step = 50;
    else if (limit > 100) step = 20;
    else if (limit > 50) step = 10;
    else if (limit > 20) step = 5;

    limit = Math.ceil(limit / step) * step;
    const ticks = [];
    for (let i = 0; i <= limit; i += step) {
      ticks.push(i);
    }
    return ticks;
  }, [maxVal]);

  const requirements = useMemo(() => {
    const N = processedData?.length || 0;
    if (N <= 1) {
      return { shouldScroll: false, chartWidth: "100%", interval: 0 };
    }
    const requiredWidth = (N - 1) * 75 + 80;
    const availableWidth = (width > 55 ? width - 55 : width) || 500;
    const shouldScroll = availableWidth < requiredWidth;
    
    return {
      shouldScroll,
      chartWidth: shouldScroll ? `${requiredWidth}px` : "100%",
      interval: 0
    };
  }, [processedData?.length, width]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          width: "100%",
          position: "relative",
        }}
      >
        {/* Frozen Left Y-Axis */}
        <div
          style={{
            width: "55px",
            flexShrink: 0,
            height: 340,
            zIndex: 2,
          }}
        >
          <ResponsiveContainer width="100%" height={340}>
            <LineChart
              data={processedData}
              margin={{
                top: 10,
                right: 0,
                left: 10,
                bottom: 20,
              }}
            >
              <YAxis
                allowDecimals={false}
                ticks={yAxisTicks}
                domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
                interval={0}
                tick={{ fontSize: 12 }}
                width={45}
              />
              <XAxis dataKey="date" hide height={85} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Scrollable Plot Area */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            overflowX: requirements.shouldScroll ? "auto" : "hidden",
            overflowY: "hidden",
            boxSizing: "border-box",
            scrollbarWidth: requirements.shouldScroll ? "auto" : "none",
            msOverflowStyle: requirements.shouldScroll ? "auto" : "none",
          }}
          className="chart-scroll-container"
        >
          <div
            style={{
              width: requirements.chartWidth,
              minWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            <ResponsiveContainer height={340}>
              <LineChart
                data={processedData}
                margin={{
                  top: 10,
                  right: 30,
                  left: 10,
                  bottom: 20,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatWeekCommencingDate}
                  tick={{ fontSize: 11 }}
                  angle={-45}
                  textAnchor="end"
                  height={85}
                  dx={-4}
                  dy={4}
                  interval={requirements.interval}
                />
                <YAxis
                  hide
                  allowDecimals={false}
                  ticks={yAxisTicks}
                  domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
                  interval={0}
                />
                <Tooltip
                  labelFormatter={formatWeekCommencingDate}
                  content={({ payload, label }) => {
                    if (payload && payload.length > 0) {
                      const total = payload.reduce(
                        (sum, item) => sum + Number(item.value),
                        0
                      );

                      return (
                        <div
                          style={{
                            backgroundColor: "white",
                            padding: "10px",
                            border: "none",
                            borderRadius: "10px",
                            boxShadow: "0px 18px 40px rgba(112, 144, 176, 0.12)",
                          }}
                        >
                          <p style={{ fontWeight: "bold", color: "#2B3674", marginBottom: "5px" }}>{formatWeekCommencingDate(label)}</p>
                          {payload.map((item, index) => (
                            <div key={index} style={{ color: item.color, fontSize: "14px", display: "flex", justifyContent: "space-between", gap: "20px" }}>
                              <span>{item.name}:</span>
                              <span style={{ fontWeight: "bold" }}>{item.value}</span>
                            </div>
                          ))}
                          <div
                            style={{
                              borderTop: "1px solid #F4F7FE",
                              marginTop: "8px",
                              paddingTop: "8px",
                              fontWeight: "bold",
                              color: "#2B3674",
                              display: "flex",
                              justifyContent: "space-between"
                            }}
                          >
                            <span>Total:</span>
                            <span>{total}</span>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                {orgKeys.map((key, index) => (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={colors[index % colors.length]}
                    strokeWidth={3}
                    dot={{ r: 4, fill: colors[index % colors.length] }}
                    name={key}
                    connectNulls={true}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Legend below the chart */}
      {orgKeys.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "15px",
            marginTop: "15px",
          }}
        >
          {orgKeys.map((key, index) => (
            <div
              key={key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <div
                style={{
                  width: "10px",
                  height: "10px",
                  backgroundColor: colors[index % colors.length],
                  borderRadius: "50%",
                }}
              />
              <span style={{ fontSize: "12px", color: "#666" }}>
                {key}
              </span>
            </div>
          ))}
        </div>
      )}

      <style>{`
        .chart-scroll-container {
          -webkit-overflow-scrolling: touch;
        }
        .chart-scroll-container::-webkit-scrollbar {
          height: 8px;
        }
        .chart-scroll-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .chart-scroll-container::-webkit-scrollbar-thumb {
          background: #c3c3c3;
          border-radius: 4px;
        }
        .chart-scroll-container::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </div>
  );
};

export default OrganizationStatsChart;
